import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Fade,
  Grow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Edit as EditIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useUser } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
// Removed direct API imports - now using cloud functions
import { theme, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';


import { useNavigate } from 'react-router-dom';
import { usePermissionCheck } from '../../components/SubscriptionGate';
import { ProductType } from '../../types/subscription';
import { generateSoulmateImage as wanxiangGenerateImage } from '../../services/wanxiangApi';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));

const GradientBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  color: 'white',
}));

const ImageContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  textAlign: 'center',
  minHeight: 400,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    minHeight: 300,
  },
}));

const SoulmatePaintingScreen = () => {
  const { user } = useAuth();
  const { birthInfo, astrolabe } = useUser();
  const { checkAccess } = usePermissionCheck();
  const hasAccess = checkAccess('soulmatepainting', 'soulmatePortrait');
  
  const navigate = useNavigate();
  
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [soulmateDescription, setSoulmateDescription] = useState<string | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [generationStep, setGenerationStep] = useState<string>('');

  // 使用后端通义万相（wan2.2-t2i-plus）生成画像
  
  // Chat and editing states
  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string, timestamp: Date}>>([]);
  const [editInput, setEditInput] = useState('');
  const [imageHistory, setImageHistory] = useState<Array<{url: string, prompt: string, timestamp: Date}>>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showChatDialog, setShowChatDialog] = useState(false);

  const generateSoulmatePainting = async (customPrompt?: string) => {
    console.log('Starting soulmate painting generation...');
    console.log('Birth info available:', !!birthInfo);
    console.log('User available:', !!user);
    
    if (!birthInfo) {
      setError('Please complete your birth information first to generate your soulmate painting.');
      return;
    }

    // 检查用户是否已登录
    if (!user) {
      setError('Please log in to generate your soulmate painting.');
      return;
    }

    // 检查订阅访问权限
    if (!hasAccess) {
      setError('需要高级会员才能使用此功能');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGenerationStep('Analyzing your astrology data...');

    try {
      let finalPrompt = customPrompt;
      
      if (!customPrompt) {
        // Step 1: 通过 AI 生成增强版灵魂伴侣描述
        console.log('Step 1: 通过 AI 生成增强版灵魂伴侣描述...');
        setGenerationStep('AI 正在分析您的命盘（约需 1-2 分钟）...');
        const descriptionResult = await generateEnhancedSoulmateDescription();
        
        if (!descriptionResult.success) {
          console.error('Failed to generate soulmate description:', descriptionResult.error);
          setError(`Failed to analyze your astrology data: ${descriptionResult.error}`);
          return;
        }
        
        finalPrompt = descriptionResult.description || '';
        setSoulmateDescription(finalPrompt);
      }
      
      // Step 2: 通过后端通义万相生成图片
      console.log('Step 2: 通过后端通义万相生成图片...');
      setGenerationStep('后端通义万相正在为您生成画像...');
      
      const imageResult = await generateImageWithWanxiang(finalPrompt || '');
      
      if (imageResult.success && imageResult.imageUrl) {
        setGeneratedImage(imageResult.imageUrl);
        
        // Add to history
        const newHistoryItem = {
          url: imageResult.imageUrl,
          prompt: finalPrompt || '',
          timestamp: new Date()
        };
        setImageHistory(prev => [newHistoryItem, ...prev]);
        
        setShowSuccess(true);
        
        // Add to chat history
        setChatMessages(prev => [...prev, 
          { role: 'user', content: finalPrompt || '', timestamp: new Date() },
          { role: 'assistant', content: '已为您生成新的灵魂伴侣画像', timestamp: new Date() }
        ]);
      }
    } catch (error: any) {
        console.error('Error editing image:', error);
        setError(`图片编辑失败：${error.message}`);
    } finally {
        setIsEditing(false);
        setEditInput('');
    }
  };

  // Enhanced function to generate detailed soulmate description using cloud function
  const generateEnhancedSoulmateDescription = async (): Promise<{success: boolean, description?: string, error?: string}> => {
    console.log('=== Debug Info ===');
    console.log('user:', user);
    console.log('birthInfo:', birthInfo);
    console.log('astrolabe:', astrolabe);
    console.log('==================');
    
    if (!astrolabe || !user) {
      const missingData = [];
      if (!user) missingData.push('user');
      if (!birthInfo) missingData.push('birthInfo');
      if (!astrolabe) missingData.push('astrolabe');
      
      console.error('Missing data:', missingData.join(', '));
      return {
        success: false,
        error: `Missing astrology data or user information: ${missingData.join(', ')}`
      };
    }

    try {
      const userGender = birthInfo.gender || 'unknown';
      const userLocation = birthInfo.location || 'unknown';
      
      console.log('Calling enhanced soulmate characteristics cloud function...');
      
      // Call the new cloud function instead of direct API call
       const response = await fetch(`${import.meta.env.VITE_VERTEX_AI_ENHANCED_SOULMATE_URL || 'https://us-central1-chinese-astrology-web.cloudfunctions.net/vertexAiEnhancedSoulmateCharacteristics'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          birthInfo: {
            gender: userGender,
            location: userLocation
          },
          astrolabeData: astrolabe,
          language: 'zh-CN'
        })
      });
      
      if (!response.ok) {
        throw new Error(`Cloud function request failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.responsePlainText) {
        console.log('Enhanced API completed successfully');
        console.log('Description length:', result.responsePlainText.length);
        
        return {
          success: true,
          description: result.responsePlainText
        };
      } else {
        console.error('Enhanced API failed:', result.error);
        return {
          success: false,
          error: result.error || 'Failed to generate enhanced description'
        };
      }
    } catch (error: any) {
      console.error('Exception during enhanced API call:', error);
      return {
        success: false,
        error: error.message || 'Unexpected error during AI analysis'
      };
    }
  };

  // 通过后端通义万相生成画像
  const generateImageWithWanxiang = async (description: string): Promise<{success: boolean, imageUrl?: string, error?: string}> => {
    try {
      const data = await wanxiangGenerateImage(description, '1024*1024');
      if ((data as any)?.url) {
        return { success: true, imageUrl: (data as any).url };
      }
      throw new Error('后端未返回图片地址');
    } catch (error: any) {
      console.error('通过通义万相生成图片失败:', error);
      return {
        success: false,
        error: error?.message || '生成图片失败'
      };
    }
  };

  // Handle chat-based image editing
  const handleEditRequest = async (editMessage: string) => {
    if (!generatedImage || !soulmateDescription) {
      setError('Please generate an initial image first.');
      return;
    }

    setIsEditing(true);
    
    // Add user message to chat
    const userMessage = {
      role: 'user' as const,
      content: editMessage,
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, userMessage]);

    try {
      // Create modified prompt based on original description and edit request
      const modifiedPrompt = `${soulmateDescription}

用户修改要求：${editMessage}

请根据用户的修改要求，调整上述描述，生成新的灵魂伴侣画像。保持原有的基本特征，但融入用户要求的修改。`;
      
      const imageResult = await generateImageWithWanxiang(modifiedPrompt);
      
      if (imageResult.success && imageResult.imageUrl) {
        setGeneratedImage(imageResult.imageUrl);
        
        // Add to history
        const newHistoryItem = {
          url: imageResult.imageUrl,
          prompt: modifiedPrompt,
          timestamp: new Date()
        };
        setImageHistory(prev => [newHistoryItem, ...prev]);
        
        // Add assistant response to chat
        const assistantMessage = {
          role: 'assistant' as const,
          content: '图片编辑成功',
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, assistantMessage]);
        
        setShowSuccess(true);
      } else {
        throw new Error(imageResult.error || 'Failed to generate modified image');
      }
    } catch (error: any) {
      console.error('Error editing image:', error);
      setError('Failed to edit image. Please try again.');
      
      // Add error message to chat
      const errorMessage = {
        role: 'assistant' as const,
        content: `图片编辑失败：${error.message}`,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsEditing(false);
      setEditInput('');
    }
  };

  // Generate image prompt from the detailed description (legacy function, kept for compatibility)
  const generateImagePromptFromDescription = (description: string): string => {
    if (!description) {
      // Fallback prompt if no description available
      const basePrompt = 'A portrait of a partner, ';
      const stylePrompt = 'photorealistic, soft lighting, gentle expression, warm and loving eyes, ';
      const astrologyPrompt = 'harmonious features, kind and compassionate appearance, ';
      const qualityPrompt = 'high quality, detailed, professional photography, 4K resolution';
      return basePrompt + stylePrompt + astrologyPrompt + qualityPrompt;
    }

    // Extract key visual characteristics from the AI response for image generation
    const basePrompt = 'A portrait of a soulmate, ';
    const stylePrompt = 'photorealistic, soft lighting, gentle expression, warm and loving eyes, ';
    
    // Convert the detailed AI analysis into image generation prompt
    const astrologyPrompt = convertAnalysisToImagePrompt(description);
    
    const qualityPrompt = 'high quality, detailed, professional photography, 4K resolution';
    
    return basePrompt + stylePrompt + astrologyPrompt + qualityPrompt;
  };



  // Helper function to convert detailed AI analysis into concise image generation prompt
  const convertAnalysisToImagePrompt = (analysis: string): string => {
    // Extract key visual elements from the detailed analysis
    // This is a simplified conversion - you might want to use more sophisticated text processing
    let prompt = '';
    
    // Look for physical appearance keywords
    const lowerAnalysis = analysis.toLowerCase();
    
    // Skin tone
    if (lowerAnalysis.includes('fair') || lowerAnalysis.includes('light skin')) {
      prompt += 'fair skin, ';
    } else if (lowerAnalysis.includes('olive') || lowerAnalysis.includes('medium skin')) {
      prompt += 'olive skin, ';
    } else if (lowerAnalysis.includes('dark') || lowerAnalysis.includes('deep skin')) {
      prompt += 'dark skin, ';
    }
    
    // Height
    if (lowerAnalysis.includes('tall')) {
      prompt += 'tall stature, ';
    } else if (lowerAnalysis.includes('petite') || lowerAnalysis.includes('short')) {
      prompt += 'petite stature, ';
    }
    
    // Body type
    if (lowerAnalysis.includes('slender') || lowerAnalysis.includes('slim')) {
      prompt += 'slender build, ';
    } else if (lowerAnalysis.includes('athletic')) {
      prompt += 'athletic build, ';
    }
    
    // Facial features
    if (lowerAnalysis.includes('gentle') || lowerAnalysis.includes('soft')) {
      prompt += 'gentle facial features, ';
    }
    if (lowerAnalysis.includes('kind eyes') || lowerAnalysis.includes('warm eyes')) {
      prompt += 'kind warm eyes, ';
    }
    
    // Personality traits that affect appearance
    if (lowerAnalysis.includes('confident')) {
      prompt += 'confident demeanor, ';
    }
    if (lowerAnalysis.includes('elegant')) {
      prompt += 'elegant appearance, ';
    }
    if (lowerAnalysis.includes('intellectual')) {
      prompt += 'intellectual look, ';
    }
    
    // Default characteristics if nothing specific found
    if (prompt === '') {
      prompt = 'harmonious features, kind and compassionate appearance, ';
    }
    
    return prompt;
  };

  const downloadImage = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = 'soulmate-painting.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const openImageDialog = () => {
    setImageDialogOpen(true);
  };

  const closeImageDialog = () => {
    setImageDialogOpen(false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.colors.background,
      paddingBottom: { xs: '80px', sm: '80px' }
    }}>
      {/* Header Section */}
      <GradientBox>
        <Box sx={{ textAlign: 'center' }}>
          <FavoriteIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            灵魂伴侣画像
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            发现命中注定的爱情
          </Typography>
        </Box>
      </GradientBox>
  
      <Box sx={{ p: responsiveSpacing }}>
        <>
            {!birthInfo ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                请先完善出生信息以生成灵魂伴侣画像
              </Alert>
            ) : (
              <>
                {!generatedImage && !soulmateDescription && (
                  <StyledCard>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        您的灵魂伴侣描述
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {soulmateDescription || '生成灵魂伴侣画像以查看描述'}
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/profile')}
                        sx={{ borderRadius: 3 }}
                      >
                        完善出生信息
                      </Button>
                    </CardContent>
                  </StyledCard>
                )}
  
                {isGenerating && (
                  <StyledCard>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={60} sx={{ mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        {generationStep || '正在创建灵魂伴侣画像'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        请耐心等待，AI正在分析您的命盘
                      </Typography>
                    </CardContent>
                  </StyledCard>
                )}
  
                {generatedImage && (
                  <Fade in={true}>
                    <StyledCard>
                      <CardContent>
                        <ImageContainer>
                          <img 
                            src={generatedImage} 
                            alt="Soulmate Painting"
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '400px', 
                              borderRadius: '12px',
                              cursor: 'pointer'
                            }}
                            onClick={() => setImageDialogOpen(true)}
                          />
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={downloadImage}
                              size="small"
                            >
                              下载
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<RefreshIcon />}
                              onClick={() => generateSoulmatePainting()}
                              size="small"
                            >
                              重新生成
                            </Button>
                          </Box>
                        </ImageContainer>
                        
                        <Paper sx={{ p: 3, mt: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                          <Typography variant="h6" gutterBottom>
                            对话式编辑画像
                          </Typography>
                          
                          {/* Chat Messages */}
                          {chatMessages.length > 0 && (
                            <Box sx={{ mb: 2, maxHeight: 200, overflowY: 'auto' }}>
                              {chatMessages.map((message, index) => (
                                <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    {message.role === 'user' ? '您' : 'AI助手'}:
                                  </Typography>
                                  <Typography variant="body2">
                                    {message.content}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                          
                          {/* Edit Input */}
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                            <TextField
                              fullWidth
                              multiline
                              maxRows={3}
                              value={editInput}
                              onChange={(e) => setEditInput(e.target.value)}
                              placeholder="描述您希望如何修改画像，例如：让她的头发更长一些，或者改变服装风格"
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                }
                              }}
                            />
                            <IconButton
                              color="inherit"
                              disabled={!editInput.trim() || isEditing}
                              onClick={() => handleEditRequest(editInput)}
                              sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                            >
                              {isEditing ? <CircularProgress size={20} /> : <SendIcon />}
                            </IconButton>
                          </Box>
                        </Paper>
                        
                        {/* Image History */}
                        {imageHistory.length > 0 && (
                          <Paper sx={{ p: 2, mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                              历史版本
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                              {imageHistory.map((historyItem, index) => (
                                <Box key={index} sx={{ minWidth: 100, textAlign: 'center' }}>
                                  <img
                                    src={historyItem.url}
                                    alt={`Version ${index}`}
                                    style={{
                                      width: 80,
                                      height: 80,
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => setGeneratedImage(historyItem.url)}
                                  />
                                  <Typography variant="caption" display="block">
                                    版本 {imageHistory.length - index}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Paper>
                        )}
                      </CardContent>
                    </StyledCard>
                  </Fade>
                )}
  
                {!isGenerating && !generatedImage && (
                  <StyledCard>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        准备好发现您的灵魂伴侣了吗？
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        基于您的紫微斗数命盘，AI将为您描绘出命中注定的灵魂伴侣形象
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<FavoriteIcon />}
                        onClick={() => generateSoulmatePainting()}
                        disabled={!birthInfo}
                        sx={{ borderRadius: 3 }}
                      >
                        {'生成灵魂伴侣画像'}
                      </Button>
                    </CardContent>
                  </StyledCard>
                )}
              </>
            )}
          </>
      </Box>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">您的灵魂伴侣画像</Typography>
        </DialogTitle>
        <DialogContent>
          {generatedImage && (
            <Box sx={{ textAlign: 'center' }}>
              <img 
                src={generatedImage} 
                alt="Soulmate Painting"
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  borderRadius: '12px'
                }}
              />
              {soulmateDescription && (
                <Typography variant="body2" sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                  {soulmateDescription}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={downloadImage} startIcon={<DownloadIcon />}>
            下载
          </Button>
          <Button onClick={() => setImageDialogOpen(false)}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        message="灵魂伴侣画像生成成功"
      />

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SoulmatePaintingScreen;